'use client';

import { Dialog } from '#/components/ui/dialog';
import { DialogContent } from '#/components/ui/scrollable-dialog';
import { ResponseCode } from '#/constants';
import { createOrder_mutationFn } from '#/data/react-query/mutations/create-order-legacy';
import {
  EthereumStakingOrderInfo,
  orderList_queryId,
} from '#/data/react-query/queries/order-list';
import { useConfiguredChainId } from '#/data/wagmi/configured';
import { directStakingAbi, useWriteDirectStakingStake } from '#/generated';
import { directStakingConfig } from '#/lib/generated';
import { handleTransactionError } from '#/lib/handle-transaction-error';
import { cn } from '#/lib/utils';
import { DialogContentProps, DialogProps } from '@radix-ui/react-dialog';
import { captureException } from '@sentry/nextjs';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useActor, useInterpret } from '@xstate/react';
import invariant from 'invariant';
import React, { useMemo } from 'react';
import { toast } from 'sonner';
import { encodeFunctionData, parseEther } from 'viem';
import { useConfig } from 'wagmi';
import { estimateGas } from 'wagmi/actions';
import machine, { MachineContext } from './contexts/staking-machine';
import { StakingDialogConfirmation } from './staking-dialog-confirmation';
import { StakingDialogInformation } from './staking-dialog-information';
import { StakingDialogTransaction } from './staking-dialog-transaction';

interface DialogContentImplProps {
  originalRow?: EthereumStakingOrderInfo;
}
function DialogContentImpl({ originalRow }: DialogContentImplProps) {
  const chainId = useConfiguredChainId();
  const config = useConfig();
  const queryClient = useQueryClient();
  const createOrder_mutation = useMutation({
    mutationFn: createOrder_mutationFn,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey.includes(orderList_queryId),
      });
    },
  });
  const { writeContractAsync: stakeAsync } = useWriteDirectStakingStake();
  const interpreter = useInterpret(machine, {
    context: originalRow
      ? {
          count: originalRow.count,
          withdrawaddr: originalRow.withdrawaddr,
          claimaddr: originalRow.claimaddr,

          paramsSig: originalRow.paramsSig,

          pubkeys: originalRow.pubkeys,
          signatures: originalRow.signatures,
          extradata: BigInt(originalRow.extradata),
          tips: BigInt(originalRow.tips),
        }
      : undefined,
    services: {
      onParamsSig: async (context, event) => {
        console.log('====================================');
        console.log('onParamsSig', context);
        console.log('====================================');
        const { count, withdrawaddr, claimaddr } = context;
        const res = await createOrder_mutation.mutateAsync({
          claimaddr: claimaddr!,
          withdrawaddr: withdrawaddr!,
          count: count!,
        });
        if (res?.data.code === ResponseCode.SUCCESS) {
          const { pubkeys, signatures, paramsSig, extradata, tips } =
            res.data.data!;
          return { pubkeys, signatures, paramsSig, extradata, tips };
        } else {
          toast.error('Error', {
            description: res?.data?.message ?? 'Uncaught error',
          });
          throw new Error(res?.data.message ?? 'Uncaught error');
        }
      },
      onTransaction: async (context, event) => {
        console.log('====================================');
        console.log('onTransaction', context);
        console.log('====================================');
        const {
          count,
          withdrawaddr,
          claimaddr,
          pubkeys,
          signatures,
          paramsSig,
          extradata,
          tips,
          overrideGasLimit,
        } = context;

        console.log('====================================');
        console.log('onTransaction', {
          count,
          withdrawaddr,
          claimaddr,
          pubkeys,
          signatures,
          paramsSig,
          extradata,
          tips,
          overrideGasLimit,
        });
        console.log('====================================');

        invariant(claimaddr != null, 'claimaddr is required');
        invariant(withdrawaddr != null, 'withdrawaddr is required');
        invariant(pubkeys != null, 'pubkeys is required');
        invariant(signatures != null, 'signatures is required');
        invariant(paramsSig != null, 'paramsSig is required');
        invariant(extradata != null, 'extradata is required');
        invariant(tips != null, 'tips is required');
        invariant(count != null, 'count is required');
        invariant(overrideGasLimit != null, 'overrideGasLimit is required');

        console.log('====================================');
        console.log('stake', {
          count,
          withdrawaddr,
          claimaddr,
          pubkeys,
          signatures,
          paramsSig,
          extradata,
          tips,
          overrideGasLimit,
        });
        console.log('====================================');

        try {
          const args = [
            claimaddr,
            withdrawaddr,
            pubkeys,
            signatures,
            paramsSig,
            extradata,
            tips,
          ] as const;

          const value = parseEther('32') * BigInt(count);

          const data = encodeFunctionData({
            abi: directStakingAbi,
            functionName: 'stake',
            args,
          });

          const gasLimit = await estimateGas(config, {
            chainId,
            to: directStakingConfig.address(chainId),
            data,
            value,
          });
          console.log('====================================');
          console.log('estimateGas', gasLimit);
          console.log('====================================');

          const transactionHash = await stakeAsync({
            args: [
              claimaddr,
              withdrawaddr,
              pubkeys,
              signatures,
              paramsSig,
              extradata,
              tips,
            ],
            value,
            gas: (gasLimit * parseEther(overrideGasLimit)) / parseEther('1'),
          });

          console.log('====================================');
          console.log('Transaction Success', transactionHash);
          console.log('====================================');

          return { transactionHash };
        } catch (error) {
          handleTransactionError(error);
          captureException(error);
          throw error;
        }
      },
    },
  });

  const [state, dispatch] = useActor(interpreter);

  const stepComponents = useMemo(
    () => ({
      information: StakingDialogInformation,
      confirmation: StakingDialogConfirmation,
      transactionReceipt: StakingDialogTransaction,
    }),
    []
  );

  const StepComponent = useMemo(() => {
    for (const [key, component] of Object.entries(stepComponents)) {
      if (state.matches(key)) {
        return component;
      }
    }
    return React.Fragment;
  }, [state, stepComponents]);

  return (
    <MachineContext.Provider value={interpreter}>
      <StepComponent />
    </MachineContext.Provider>
  );
}

export function StakingDialogContent({
  originalRow,
  className,
  ...props
}: DialogContentImplProps & DialogContentProps) {
  return (
    <DialogContent
      {...props}
      className={cn(
        'sm:max-w-(--breakpoint-sm) overflow-auto max-h-screen',
        className
      )}
    >
      <DialogContentImpl originalRow={originalRow} />
    </DialogContent>
  );
}

export function StakingDialog({
  originalRow,
  ...props
}: DialogContentImplProps & DialogProps) {
  return (
    <Dialog {...props}>
      <StakingDialogContent originalRow={originalRow} />
    </Dialog>
  );
}
